import React, { useEffect, useMemo, useState } from 'react';
import { posApi } from '../services/api';

const currency = (v) => `₦${Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ReportsPage() {
    const [report, setReport] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [range, setRange] = useState(() => {
        const today = new Date();
        const yyyyMmDd = today.toISOString().slice(0, 10);
        return { start_date: yyyyMmDd, end_date: yyyyMmDd };
    });

    const loadData = async () => {
        setLoading(true);
        const [reportRes, txRes] = await Promise.all([
            posApi.dailyReport(range),
            posApi.transactions({ limit: 20, start_date: range.start_date, end_date: range.end_date }),
        ]);

        if (reportRes.success) setReport(reportRes.data);
        if (txRes.success) setTransactions(txRes.data?.data || []);
        setLoading(false);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const summary = report?.summary || {};
    const margin = useMemo(() => Number(summary.gross_margin_percent || 0), [summary.gross_margin_percent]);
    const reportCards = [
        { label: 'Cash In Hand', value: currency(summary.cash_in_hand), tone: 'green' },
        { label: 'At Bank', value: currency(summary.bank_inflow), tone: 'blue' },
        { label: 'Income', value: currency(summary.income), tone: 'gold' },
        { label: 'Revenue Excl. Tax', value: currency(summary.net_revenue), tone: 'slate' },
        { label: 'Gross Revenue', value: currency(summary.gross_revenue), tone: 'blue' },
        { label: 'Gross Profit', value: currency(summary.gross_profit), tone: 'green' },
        { label: 'COGS', value: currency(summary.cogs), tone: 'red' },
        { label: 'Tax Collected', value: currency(summary.tax_collected), tone: 'slate' },
        { label: 'Discounts', value: currency(summary.total_discount), tone: 'gold' },
        { label: 'Refunds', value: currency(summary.refund_total), tone: 'red' },
        { label: 'Transactions', value: Number(summary.transaction_count || 0).toLocaleString('en-NG'), tone: 'slate' },
        { label: 'Average Ticket', value: currency(summary.average_transaction), tone: 'blue' },
    ];
    const maxProductSales = Math.max(...(report?.top_products || []).map((item) => Number(item.total_sales || 0)), 1);

    return (
        <div className="reports-page">
            <div className="reports-hero">
                <div>
                    <h2>Reports & Analytics</h2>
                    <p>Track revenue, cash flow, product performance, payment mix, and recent sales activity.</p>
                </div>
                <div className="reports-filter-row">
                    <input
                        type="date"
                        value={range.start_date}
                        onChange={(e) => setRange((prev) => ({ ...prev, start_date: e.target.value }))}
                    />
                    <input
                        type="date"
                        value={range.end_date}
                        onChange={(e) => setRange((prev) => ({ ...prev, end_date: e.target.value }))}
                    />
                    <button className="btn btn-sm" onClick={loadData} disabled={loading}>
                        {loading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>
            </div>

            <div className="reports-kpi-grid">
                {reportCards.map((card) => (
                    <article className={`reports-kpi-card tone-${card.tone}`} key={card.label}>
                        <span>{card.label}</span>
                        <strong>{card.value}</strong>
                    </article>
                ))}
            </div>

            <div className="reports-insight-grid">
                <section className="reports-panel reports-margin-panel">
                    <div>
                        <h3>Growth Snapshot</h3>
                        <p>Gross margin for selected period</p>
                    </div>
                    <strong>{margin.toFixed(2)}%</strong>
                    <div className="reports-meter"><span style={{ width: `${Math.min(100, Math.max(0, margin))}%` }} /></div>
                </section>

                <section className="reports-panel">
                    <h3>Payment Breakdown</h3>
                    <div className="reports-list">
                        {(report?.payment_breakdown || []).length === 0 ? (
                            <p className="muted-sm">No payment data for this period.</p>
                        ) : (report?.payment_breakdown || []).map((item) => (
                            <div className="reports-list-row" key={item.payment_method}>
                                <span>{item.payment_method || 'Unknown'}</span>
                                <b>{currency(item.total_amount)}</b>
                                <small>{item.transaction_count} transactions</small>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="reports-panel">
                <h3>Top Products</h3>
                <div className="reports-product-bars">
                    {(report?.top_products || []).length === 0 ? (
                        <p className="muted-sm">No product sales for this period.</p>
                    ) : (report?.top_products || []).map((item) => (
                        <div className="reports-product-row" key={item.product_id}>
                            <div>
                                <strong>{item.name}</strong>
                                <span>{Number(item.quantity_sold || 0).toLocaleString('en-NG')} sold</span>
                            </div>
                            <b>{currency(item.total_sales)}</b>
                            <div className="reports-meter"><span style={{ width: `${(Number(item.total_sales || 0) / maxProductSales) * 100}%` }} /></div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="reports-panel">
                <h3>Recent Transactions</h3>
                <div className="reports-table-wrap">
                <table className="reports-table">
                    <thead>
                        <tr>
                            <th>Receipt</th>
                            <th>Payment</th>
                            <th>Total</th>
                            <th>Status</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactions.map((t) => (
                            <tr key={t.id}>
                                <td>{t.transaction_id || String(t.id).slice(0, 8)}</td>
                                <td>{t.payment_method}</td>
                                <td>{currency(t.total_amount)}</td>
                                <td>{t.status}</td>
                                <td>{new Date(t.created_at).toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </section>
        </div>
    );
}
